/**
 * v109.9.179 — Tiny in-page pub/sub used to nudge related widgets after a
 * mutation (e.g. Accounts edits a settlement → the dashboard's outstanding
 * tile, the Approval tab, the employee's own Settlement tab all reload on
 * their next tick).
 *
 * This is intentionally NOT a global store — it just broadcasts a topic +
 * payload. Consumers keep their own state and choose how to react (usually
 * by calling their existing `load()`).
 *
 * Topics currently used:
 *   • `travel:settlement:updated`  → { id, record }
 *   • `travel:advance:updated`     → { id, record }
 *   • `travel:disbursed`           → { id, record }
 *   • `travel:ledgered`            → { id, record }
 */

const PREFIX = "erp:";

export function emitDataChange(topic, payload = {}) {
  try {
    window.dispatchEvent(new CustomEvent(`${PREFIX}${topic}`, { detail: payload }));
  } catch (_) { /* SSR / stale window — ignore */ }
}

/**
 * Subscribe to a topic. Returns an unsubscribe fn.
 *   const off = onDataChange("travel:settlement:updated", ({ id }) => load());
 *   useEffect(() => off, []);   // cleanup
 */
export function onDataChange(topic, handler) {
  const listener = (e) => { try { handler(e?.detail || {}); } catch (_) { /* ignore */ } };
  window.addEventListener(`${PREFIX}${topic}`, listener);
  return () => window.removeEventListener(`${PREFIX}${topic}`, listener);
}

/**
 * React helper — auto-subscribes for the component's lifetime.
 * Pass a topic (or array of topics) + handler. Handler always sees the
 * latest reference thanks to a ref, so callers don't need useCallback.
 */
export function useDataChange(topicOrTopics, handler) {
  // Import lazily so this file can be used in non-React contexts too.
  const { useEffect, useRef } = require("react");
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    const topics = Array.isArray(topicOrTopics) ? topicOrTopics : [topicOrTopics];
    const offs = topics.map((t) => onDataChange(t, (d) => ref.current?.(d)));
    return () => offs.forEach((off) => off());
  }, [Array.isArray(topicOrTopics) ? topicOrTopics.join("|") : topicOrTopics]);
}
